/**
 * My Success Timeline Page
 *
 * Visual timeline showing user progress across all activities:
 * NPC progression, practice sessions, coaching, training, certifications.
 *
 * Route: /my/timeline
 */

import { useState, useMemo } from 'react';
import {
  useGetMyTimelineQuery,
  useGetMyTimelineStatsQuery,
  type TimelineEventType,
  type UserTimelineEvent,
  TIMELINE_ICONS,
  TIMELINE_EVENT_LABELS,
  TIMELINE_EVENT_COLORS,
} from '../store/api/timelineApi';

type FilterCategory = 'all' | 'npc' | 'practice' | 'coaching' | 'certifications' | 'training' | 'credentials' | 'hiring';

const FILTER_OPTIONS: { id: FilterCategory; label: string; types: TimelineEventType[] }[] = [
  { id: 'all', label: 'All', types: [] },
  { id: 'npc', label: 'NPC', types: ['npc_level_passed'] },
  { id: 'practice', label: 'Practice', types: ['practice_completed'] },
  { id: 'coaching', label: 'Coaching', types: ['coaching_session_completed'] },
  {
    id: 'certifications',
    label: 'Certifications',
    types: ['certification_earned', 'badge_earned', 'authority_rank_up'],
  },
  { id: 'training', label: 'Org Training', types: ['training_completed', 'org_milestone'] },
  { id: 'credentials', label: 'Credentials', types: ['credential_issued'] },
  { id: 'hiring', label: 'Hiring', types: ['application_submitted', 'candidate_hired', 'candidate_rejected'] },
];

export function MySuccessTimelinePage() {
  const [filter, setFilter] = useState<FilterCategory>('all');
  const [limit, setLimit] = useState(20);

  const selectedFilter = FILTER_OPTIONS.find((f) => f.id === filter) || FILTER_OPTIONS[0];
  const queryTypes = selectedFilter.types.length > 0 ? selectedFilter.types : undefined;

  const { data: timelineData, isLoading: loadingTimeline } = useGetMyTimelineQuery({
    limit,
    types: queryTypes,
  });
  const { data: stats } = useGetMyTimelineStatsQuery();

  const events = timelineData?.events || [];

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups: Record<string, UserTimelineEvent[]> = {};
    for (const event of events) {
      const date = new Date(event.occurredAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    }
    return groups;
  }, [events]);

  const handleLoadMore = () => {
    setLimit((prev) => prev + 20);
  };

  if (loadingTimeline) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Success Timeline</h1>
          <p className="mt-1 text-gray-500">Track your growth and achievements</p>
        </div>
        {stats && (
          <div className="text-right">
            <div className="text-2xl font-bold text-indigo-600">{stats.totalEvents}</div>
            <div className="text-sm text-gray-500">Total Events</div>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="NPC Levels"
            value={stats.byType.npc_level_passed}
            icon={TIMELINE_ICONS.trophy}
            color="amber"
          />
          <StatCard
            label="Practice Sessions"
            value={stats.byType.practice_completed}
            icon={TIMELINE_ICONS.microphone}
            color="blue"
          />
          <StatCard
            label="Certifications"
            value={stats.byType.certification_earned + stats.byType.badge_earned}
            icon={TIMELINE_ICONS.ribbon}
            color="indigo"
          />
          <StatCard
            label="Training Complete"
            value={stats.byType.training_completed}
            icon={TIMELINE_ICONS.school}
            color="teal"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {FILTER_OPTIONS.map((option) => (
          <button
            key={option.id}
            onClick={() => setFilter(option.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === option.id
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

        {Object.keys(groupedEvents).length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No events found.</p>
            <p className="text-sm mt-2">
              Complete practice sessions, pass NPC levels, or earn certifications to see them here.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedEvents).map(([date, dateEvents]) => (
              <div key={date} className="relative">
                {/* Date header */}
                <div className="sticky top-0 z-10 bg-gray-50 py-2 px-4 ml-12 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-700">{date}</h3>
                </div>

                {/* Events for this date */}
                <div className="space-y-4 mt-4">
                  {dateEvents.map((event) => (
                    <TimelineEventCard key={event.id} event={event} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Load More */}
      {events.length >= limit && (
        <div className="text-center">
          <button
            onClick={handleLoadMore}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Load More Events
          </button>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: string;
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    teal: 'bg-teal-50 text-teal-700',
    green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700',
  };

  return (
    <div className={`rounded-lg p-4 ${colorClasses[color] || 'bg-gray-50 text-gray-700'}`}>
      <div className="flex items-center gap-2">
        <span className="text-2xl">{icon}</span>
        <span className="text-2xl font-bold">{value}</span>
      </div>
      <div className="text-sm mt-1">{label}</div>
    </div>
  );
}

function TimelineEventCard({ event }: { event: UserTimelineEvent }) {
  const icon = TIMELINE_ICONS[event.iconKey] || '📌';
  const label = TIMELINE_EVENT_LABELS[event.type] || 'Event';
  const colorKey = TIMELINE_EVENT_COLORS[event.type] || 'gray';

  const colorClasses: Record<string, { bg: string; border: string; text: string }> = {
    amber: { bg: 'bg-amber-100', border: 'border-amber-400', text: 'text-amber-700' },
    blue: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700' },
    purple: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700' },
    green: { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-700' },
    indigo: { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-700' },
    pink: { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-700' },
    teal: { bg: 'bg-teal-100', border: 'border-teal-400', text: 'text-teal-700' },
    cyan: { bg: 'bg-cyan-100', border: 'border-cyan-400', text: 'text-cyan-700' },
    emerald: { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-700' },
    slate: { bg: 'bg-slate-100', border: 'border-slate-400', text: 'text-slate-700' },
  };

  const colors = colorClasses[colorKey] || colorClasses.slate;
  const time = new Date(event.occurredAt).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  const isHighlighted =
    event.type === 'authority_rank_up' ||
    event.type === 'certification_earned' ||
    event.type === 'npc_level_passed' ||
    event.type === 'credential_issued' ||
    event.type === 'candidate_hired';

  return (
    <div className="relative flex gap-4 ml-4">
      {/* Icon circle */}
      <div
        className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${colors.bg} border-2 ${colors.border}`}
      >
        <span className="text-sm">{icon}</span>
      </div>

      {/* Content */}
      <div
        className={`flex-1 rounded-lg border p-4 ${
          isHighlighted ? `${colors.bg} ${colors.border}` : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}
              >
                {label}
              </span>
              {event.visibility === 'public' && (
                <span className="text-xs text-gray-400">Public</span>
              )}
            </div>
            <h4 className="mt-1 font-semibold text-gray-900">{event.title}</h4>
            {event.description && (
              <p className="mt-1 text-sm text-gray-600">{event.description}</p>
            )}

            {/* Metadata */}
            <EventMetadata event={event} />
          </div>

          <span className="text-xs text-gray-400 ml-4 whitespace-nowrap">{time}</span>
        </div>
      </div>
    </div>
  );
}

function EventMetadata({ event }: { event: UserTimelineEvent }) {
  const { metadata } = event;
  if (!metadata) return null;

  const items: { label: string; value: string | number }[] = [];

  // Score
  if (metadata.scoreOverall !== undefined) {
    items.push({ label: 'Score', value: `${metadata.scoreOverall.toFixed(1)}` });
  }

  // NPC info
  if (metadata.npcPersonaLabel) {
    items.push({ label: 'Persona', value: metadata.npcPersonaLabel });
  }
  if (metadata.xpAwarded) {
    items.push({ label: 'XP', value: `+${metadata.xpAwarded}` });
  }

  // Coaching info
  if (metadata.coachName) {
    items.push({ label: 'Coach', value: metadata.coachName });
  }

  // Training info
  if (metadata.orgName) {
    items.push({ label: 'Organization', value: metadata.orgName });
  }
  if (metadata.moduleName) {
    items.push({ label: 'Module', value: metadata.moduleName });
  }

  // Authority changes
  if (metadata.authorityTierBefore && metadata.authorityTierAfter) {
    items.push({
      label: 'Rank',
      value: `${metadata.authorityTierBefore} → ${metadata.authorityTierAfter}`,
    });
  }

  // Session duration
  if (metadata.sessionDurationSeconds) {
    const minutes = Math.round(metadata.sessionDurationSeconds / 60);
    items.push({ label: 'Duration', value: `${minutes} min` });
  }

  if (items.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-3">
      {items.map(({ label, value }) => (
        <div key={label} className="text-xs">
          <span className="text-gray-400">{label}:</span>{' '}
          <span className="font-medium text-gray-700">{value}</span>
        </div>
      ))}
    </div>
  );
}

export default MySuccessTimelinePage;
